import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaHome,
  FaChevronRight,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaPaperPlane,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import { createInquiryDetails } from "../api/InquiryDetails";
import Swal from "sweetalert2";
import "../Vender/css/Contact.css";

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);

  const [inquiryDetails, setInquiryDetails] = useState({
    name: "",
    email: "",
    phone_no: "",
    message: "",
  });

  const handleChange = (e) => {
    setInquiryDetails({
      ...inquiryDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const response = createInquiryDetails(inquiryDetails);
    response
      .then((res) => {
        if (res.status === 200) {
          Swal.fire({
            title: "Message Sent!",
            text: "We'll get back to you within 24 hours.",
            icon: "success",
            timer: 2000,
          });
          setInquiryDetails({
            name: "",
            email: "",
            phone_no: "",
            message: "",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          title: "Oops!",
          text: "Something went wrong. Please try again.",
          icon: "error",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      details: [
        "1209, Shilp Zaveri,",
        "Shyamal Cross Road,",
        "NehruNagar,Ahmedabad,",
        "Gujarat 380015",
      ],
      color: "#6366F1",
    },
    {
      icon: <FaPhoneAlt />,
      title: "Call Us",
      details: ["+91 9898143720", "+91 8866051209"],
      links: ["tel:+919898143720", "tel:+918866051209"],
      color: "#10B981",
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      details: ["aacoreholidays@gmail.com.com", "Info@aacoreholidayresort.com"],
      links: [
        "mailto:aacoreholidays@gmail.com",
        "mailto:Info@aacoreholidayresort.com",
      ],
      color: "#F59E0B",
    },
    {
      icon: <FaClock />,
      title: "Working Hours",
      details: ["Mon - Fri: 10:30 AM - 6:30 PM", "Sat: 10:30 AM - 2:30 PM"],
      color: "#EC4899",
    },
  ];

  return (
    <section
      className={`contact-page ${isVisible ? "visible" : ""}`}
      ref={sectionRef}
    >
      {/* Hero Banner */}
      <div className="contact-hero">
        <div className="hero-bg-pattern"></div>
        <div className="hero-content">
          <nav className="contact-breadcrumb">
            <Link to="/" className="bc-link">
              <FaHome />
              <span>Home</span>
            </Link>
            <FaChevronRight className="bc-sep" />
            <span className="bc-current">Contact</span>
          </nav>
          <h1 className="hero-title">Get In Touch</h1>
          <p className="hero-desc">
            We'd love to hear from you. Let's start a conversation.
          </p>
        </div>
        <div className="hero-wave">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="#F8FAFC"
            />
          </svg>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="contact-info-section">
        <div className="info-container">
          <div className="info-grid">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="info-card"
                style={{
                  "--card-color": info.color,
                  "--delay": `${index * 0.1}s`,
                }}
              >
                <div className="info-icon">{info.icon}</div>
                <h3 className="info-title">{info.title}</h3>
                <div className="info-details">
                  {info.details.map((detail, idx) =>
                    info.links ? (
                      <a key={idx} href={info.links[idx]} className="info-link">
                        {detail}
                      </a>
                    ) : (
                      <p key={idx}>{detail}</p>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Contact Section */}
      <div className="contact-main">
        <div className="contact-container">
          {/* Section Header */}
          <div className="section-header">
            <span className="header-badge">Contact Us</span>
            <h2 className="header-title">Send Us a Message</h2>
            <p className="header-desc">
              Fill out the form below and our team will get back to you within
              24 hours.
            </p>
          </div>

          <div className="contact-grid">
            {/* Form Section */}
            <div className="form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                {/* Name Field */}
                <div
                  className={`form-group ${focusedField === "name" || inquiryDetails.name ? "focused" : ""}`}
                >
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={inquiryDetails.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("name")}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email Field */}
                <div
                  className={`form-group ${focusedField === "email" || inquiryDetails.email ? "focused" : ""}`}
                >
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={inquiryDetails.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="Enter your email address"
                  />
                </div>

                {/* Phone Field */}
                <div
                  className={`form-group ${focusedField === "phone_no" || inquiryDetails.phone_no ? "focused" : ""}`}
                >
                  <label htmlFor="phone_no">Phone Number</label>
                  <input
                    type="tel"
                    id="phone_no"
                    name="phone_no"
                    value={inquiryDetails.phone_no}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("phone_no")}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                {/* Message Field */}
                <div
                  className={`form-group full-width ${focusedField === "message" || inquiryDetails.message ? "focused" : ""}`}
                >
                  <label htmlFor="message">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={inquiryDetails.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="Write your message here..."
                    rows="5"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="form-submit">
                  <button
                    type="submit"
                    className={`submit-btn ${isSubmitting ? "submitting" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <FaPaperPlane />
                      </>
                    )}
                  </button>
                  <p className="form-note">
                    <FaCheckCircle />
                    <span>
                      Your information is secure and will never be shared.
                    </span>
                  </p>
                </div>
              </form>
            </div>

            {/* Map / Image Section */}
            <div className="map-wrapper">
              <div className="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3672.225825219111!2d72.52945631202596!3d23.015479179091976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x379c6c297f1358f%3A0x70d7b2efcd01ec85!2sAacore%20Holidays%20Resort%20and%20Hospitality!5e0!3m2!1sen!2sin!4v1786341867197!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="Office Location"
                ></iframe>
              </div>
              <div className="map-overlay">
                <div className="overlay-content">
                  <h4>Our Office</h4>
                  <p>1209, Shilp Zaveri, Shyamal Cross Rd, Ahmedabad</p>
                  <a
                    href="https://www.google.com/maps?q=Aacore+Holidays+Resort+and+Hospitality"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="directions-btn"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      {/* <div className="contact-cta">
        <div className="cta-container">
          <div className="cta-content">
            <h3>Prefer to Talk?</h3>
            <p>Our team is available to assist you with any questions.</p>
          </div>
          <div className="cta-actions">
            <a href="tel:+919898143720" className="cta-btn phone">
              <FaPhoneAlt />
              <span>+91 9898143720</span>
            </a>
            <a
              href="mailto:info@aacoreholidaysresort.com"
              className="cta-btn email"
            >
              <FaEnvelope />
              <span>Email Us</span>
            </a>
          </div>
        </div>
      </div> */}
    </section>
  );
};

export default Contact;
